import { useEffect, useState, useRef, useCallback } from "react";
import { useField } from "formik";
import "./SelectStudents.css";
import SelectStudentTable from "./SelectStudentTable";
import StudentSelectorSkeleton from "./StudentSelectorSkeleton";
import { useGetStudentsByTeacher } from "../../../../hooks/teacher/students/useGetStudentsByTeacher";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import { useDebounce } from "../../../../utils/useDebounce";
import TableError from "../../common/TableError";

/**
 * @typedef {"searching" | "infinite-scroll" | "initial-load" | "none"} LoadingType
 */

/**
 * StudentSelector Component
 * A reusable component for students in classroom forms (Create/Update)
 *
 * @param {Object} props
 * @param {string} props.name - Formik field name for selected students
 * @param {Array} [props.initialSelectedStudents=[]] - Pre-selected students (for edit mode)
 * @param {boolean} [props.isEdit=false] - Flag indicating if the component is in edit mode
 * @param {boolean} [props.refresh=false] - Boolean variable to indicate that we need to refresh the list so that we get the updated sutdent after creating the student
 * @param {Object} [props.setClassroom] - Classroom detail object
 */
const StudentSelector = ({
  name,
  initialSelectedStudents = [],
  refresh = false,
  setClassroom,
}) => {
  const [field, meta, helpers] = useField(name);
  const {
    students,
    loading,
    error,
    meta: paginationMeta,
    getStudentsByTeacher,
  } = useGetStudentsByTeacher();

  // Search and filtering state
  const [searchQuery, setSearchQuery] = useState("");

  // Use custom debounce hook for cleaner implementation
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [allStudents, setAllStudents] = useState([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Selection state
  const [checkedStudents, setCheckedStudents] = useState([]);

  // Refs for infinite scroll and preventing duplicate API calls
  const observerTarget = useRef(null);

  /** @type {React.MutableRefObject<LoadingType>} */
  const loadingRef = useRef("none");

  /**
   * Handle search input changes
   * @param {string} query - Search query string
   */
  const handleSearchChange = (query) => {
    setSearchQuery(query);
  };

  /**
   * @callback FetchStudentsCallback
   * @param {number} pageNumber - Page number to fetch
   * @param {boolean} isNewSearch - Whether this is a new search
   * @param {LoadingType} loadingType - Type of loading
   * @returns {Promise<void>}
   */

  /** @type {FetchStudentsCallback} */
  const fetchStudents = useCallback(
    async (pageNumber, isNewSearch = false, loadingType) => {
      // Prevent duplicate API calls
      if (loadingRef.current !== "none") {
        return;
      }

      loadingRef.current = loadingType;

      try {
        // Build filters object for API call
        const filters = {};
        if (debouncedSearchQuery.trim()) {
          // Search by name or student_code
          filters.name = debouncedSearchQuery.trim();
        }

        filters.status = 1;

        // Make API call with filters
        await getStudentsByTeacher({
          page: pageNumber,
          per_page: ITEMS_PER_PAGE,
          filters,
          unassigned_students: true,
        });

        // Update page number after successful fetch
        setCurrentPage(pageNumber);

        // If it's a new search, clear the checked students
        if (isNewSearch) {
          setCheckedStudents([]);
        }

        // Mark initial load as complete
        if (isInitialLoad) {
          setIsInitialLoad(false);
        }
      } catch (err) {
      } finally {
        loadingRef.current = "none";
        setIsLoadingMore(false);
      }
    },
    [debouncedSearchQuery, getStudentsByTeacher, isInitialLoad],
  );

  /**
   * Load more students for infinite scroll
   * Only loads if not already loading and more pages available
   */
  const loadMore = useCallback(() => {
    if (loadingRef.current === "none" && hasMorePages && !isLoadingMore) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      fetchStudents(nextPage, false, "infinite-scroll");
    }
  }, [currentPage, hasMorePages, isLoadingMore, fetchStudents]);

  /**
   * Effect: Handle external refresh trigger
   * Refreshes the student list when parent component requests it
   */
  useEffect(() => {
    if (hasInitialized) {
      setCurrentPage(1);
      setAllStudents([]);
      setHasMorePages(true);
      setCheckedStudents([]);
      fetchStudents(1, true, "initial-load");
    }
  }, [refresh]);

  /**
   * Effect: Fetch initial students on mount
   */
  useEffect(() => {
    if (!hasInitialized) {
      fetchStudents(1, true, "initial-load");
      setHasInitialized(true);
    }
  }, [hasInitialized, fetchStudents]);

  /**
   * Effect: Handle debounced search query changes
   * Resets to page 1 when search query changes
   */
  useEffect(() => {
    // Skip if this is the initial render or not initialized
    if (!hasInitialized || isInitialLoad) {
      return;
    }

    // Reset pagination and fetch new results
    setCurrentPage(1);
    setAllStudents([]);
    setHasMorePages(true);
    fetchStudents(1, true, "searching");
  }, [debouncedSearchQuery]);

  /**
   * Effect: Update students list when API response changes
   * Handles both initial load and pagination
   */
  useEffect(() => {
    if (students) {
      if (currentPage === 1) {
        // Replace all students for new search or initial load
        setAllStudents(students);
      } else {
        // Append students for pagination (infinite scroll)
        setAllStudents((prev) => {
          // Prevent duplicates by checking IDs
          const existingIds = new Set(prev.map((s) => s.id));
          const newStudents = students.filter((s) => !existingIds.has(s.id));
          return [...prev, ...newStudents];
        });
      }

      // Update pagination state
      setHasMorePages(
        paginationMeta
          ? paginationMeta.current_page < paginationMeta.last_page
          : false,
      );
    }
  }, [students, currentPage, paginationMeta]);

  /**
   * Effect: Initialize selected students for edit mode
   */
  useEffect(() => {
    if (
      initialSelectedStudents &&
      initialSelectedStudents.length > 0 &&
      !field.value?.length
    ) {
      const selectedWithFullData = initialSelectedStudents.map((student) => ({
        id: student.id,
        username: student.username,
        name: student.name,
        student_code: student.student_code,
      }));
      helpers.setValue(selectedWithFullData);
    }
  }, [initialSelectedStudents]);

  /**
   * Intersection Observer for infinite scroll
   * Observes the target element and loads more when it becomes visible
   */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMorePages &&
          loadingRef.current === "none" &&
          !isLoadingMore
        ) {
          loadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: "100px",
      },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMorePages, isLoadingMore, loadMore]);

  /**
   * Get selected student IDs for filtering
   * @returns {Array<number>} Array of selected student IDs
   */
  const getSelectedStudentIds = () => {
    return (field.value || []).map((student) => student.id);
  };

  /**
   * Toggle individual student checkbox (intermediate state)
   * @param {number} studentId - ID of the student to toggle
   */
  const toggleStudentCheck = (studentId) => {
    setCheckedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  /**
   * Select/Deselect all visible students (excluding already selected ones)
   */
  const toggleSelectAll = () => {
    const availableStudents = getAvailableStudents();

    const availableIds = availableStudents.map((s) => s.id);

    // Check if all available students are checked
    const allChecked = availableIds.every((id) => checkedStudents.includes(id));

    if (allChecked) {
      // Uncheck all available students
      setCheckedStudents((prev) =>
        prev.filter((id) => !availableIds.includes(id)),
      );
    } else {
      // Check all available students
      setCheckedStudents((prev) => {
        const newChecked = [...prev];
        availableIds.forEach((id) => {
          if (!newChecked.includes(id)) {
            newChecked.push(id);
          }
        });
        return newChecked;
      });
    }
  };

  /**
   * Add checked students to selected list
   * Moves students from available to selected and clears checkboxes
   */
  const addStudents = () => {
    if (checkedStudents.length === 0) return;

    const studentsToAdd = allStudents.filter((s) =>
      checkedStudents.includes(s.id),
    );

    if (initialSelectedStudents && initialSelectedStudents?.data?.length > 0) {
      initialSelectedStudents.data.forEach((s) => {
        if (checkedStudents.includes(s.id)) {
          studentsToAdd.push(s);
        }
      });
    }

    const currentSelected = field.value || [];

    // Create new selected list, avoiding duplicates
    const existingIds = new Set(currentSelected.map((s) => s.id));
    const newStudents = studentsToAdd
      .filter((student) => !existingIds.has(student.id))
      .map((student) => ({
        username: student.username,
        id: student.id,
        name: student.name,
        student_code: student.student_code,
      }));

    if (newStudents.length > 0) {
      helpers.setValue([...currentSelected, ...newStudents]);
    }

    // Clear checkboxes after adding
    setCheckedStudents([]);
    // Loading More student if user add the students to list beause if user select all the student
    // and never reach the end no more students which fech and after selecting no student will be
    // shown, even though their may be more students
    loadMore();
  };

  /**
   * Remove student from selected list
   * @param {number} studentId - ID of the student to remove
   */
  const removeStudent = (studentId) => {
    const updatedStudents = field.value.filter((s) => s.id !== studentId);
    helpers.setValue(updatedStudents);

    // Also remove from checked students if present
    setCheckedStudents((prev) => prev.filter((id) => id !== studentId));
  };

  /**
   * Get available students (exclude already selected ones)
   * @returns {Array} Array of available students
   */
  const getAvailableStudents = () => {
    if (!allStudents) return [];
    const selectedIds = new Set(getSelectedStudentIds());

    let allStudent = allStudents.filter(
      (student) => !selectedIds.has(student.id),
    );
    if (initialSelectedStudents && initialSelectedStudents?.data?.length > 0) {
      allStudent = [
        ...allStudent,
        ...initialSelectedStudents.data.filter(
          (student) => !selectedIds.has(student.id),
        ),
      ];
    }

    return allStudent;
  };

  /**
   * Get selected students for display
   * @returns {Array} Array of selected students with required properties
   */
  const getSelectedStudentsForDisplay = () => {
    return (field.value || []).map((student) => ({
      id: student.id,
      username: student.username,
      name: student.name,
      student_code: student.student_code,
    }));
  };

  // Show loading state only on initial load
  if (isInitialLoad && loading) {
    return (
      <div>
        {/* Search Bar Skeleton */}
        <div className="student-selector-search-wrapper">
          <label className="student-selector-label">Students</label>
          <div className="student-selector-search w-full">
            <i className="bi bi-search"></i>
            <input
              type="text"
              placeholder="Search by name..."
              disabled
              className="student-selector-search-input"
              style={{ opacity: 0.6 }}
            />
          </div>
        </div>

        <div className="selectStudent-container">
          {/* Available Students Skeleton */}
          <div className="select-table-wrapper">
            <StudentSelectorSkeleton rows={5} type="select" />
          </div>

          {/* Selected Students Skeleton */}
          <div className="select-table-wrapper">
            <StudentSelectorSkeleton rows={3} type="selected" />
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !allStudents.length) {
    return (
      <TableError
        error={error}
        handleRefresh={() => {
          getStudentsByTeacher({ unassigned_students: true });
        }}
        loading={loading}
      />
    );
  }

  const availableStudents = getAvailableStudents();
  const selectedStudents = getSelectedStudentsForDisplay();

  return (
    <div>
      {/* Search Bar */}
      <div className="student-selector-search-wrapper">
        <label className="student-selector-label">Students</label>
        <div className="student-selector-search w-full">
          <i className="bi bi-search"></i>
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="student-selector-search-input"
            disabled={loading && !isInitialLoad}
          />
        </div>

        <div>
          {loading && !isInitialLoad && (
            <div className="" role="status">
              <span className="">Searching...</span>
            </div>
          )}
        </div>
      </div>

      <div className="selectStudent-container">
        {/* Available Students Table */}
        <div className="select-table-wrapper">
          <SelectStudentTable
            students={availableStudents}
            type="select"
            checkedStudents={checkedStudents}
            toggleStudentCheck={toggleStudentCheck}
            toggleSelectAll={toggleSelectAll}
            addStudents={addStudents}
            observerTarget={observerTarget}
            isLoadingMore={isLoadingMore}
            hasMore={hasMorePages}
            isSearching={loading && !isInitialLoad}
            searchQuery={searchQuery}
          />
        </div>

        {/* Selected Students Table */}
        <div className="select-table-wrapper">
          <SelectStudentTable
            students={selectedStudents}
            type="selected"
            removeStudent={removeStudent}
            selectedCount={selectedStudents.length}
          />
        </div>
      </div>

      {/* Formik Validation Error */}
      {meta.touched && meta.error && (
        <div className="invalid-feedback d-block mt-2">
          <i className="bi bi-exclamation-circle-fill me-1"></i>
          {meta.error}
        </div>
      )}
    </div>
  );
};

export default StudentSelector;
